import { useCallback, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { fetchVendorInvoices } from "../../../utils/agentsApi";
import {
    JOB_LIST_PAGE_SIZE,
    JOB_STATUS,
    PROCESSING_LIST_STATUS,
    VENDOR_INVOICE_POLLING,
    VENDOR_INVOICE_QUERY_KEYS,
} from "./utils";

const INITIAL_PAGES = Object.freeze({
    pendingVendor: 1,
    pendingReview: 1,
    failed: 1,
    processing: 1,
    archived: 1,
});

const useJobsPage = ({
    status,
    page,
    pageSize,
    refetchInterval,
    setPage,
}) => {
    const offset = (page - 1) * pageSize;
    const query = useQuery({
        queryKey: VENDOR_INVOICE_QUERY_KEYS.jobsByStatus(status, { limit: pageSize, offset }),
        queryFn: () => fetchVendorInvoices(status, { limit: pageSize, offset }),
        refetchInterval,
        refetchOnWindowFocus: true,
    });

    const total = query.data?.total ?? 0;
    const pageCount = Math.max(1, Math.ceil(total / pageSize) || 1);

    return {
        jobs: query.data?.items ?? [],
        total,
        loading: query.isLoading,
        page,
        pageCount,
        pageSize,
        setPage,
        isError: query.isError,
        error: query.error,
    };
};

/**
 * Paged job lists for the vendor invoice listing page (no Active/Finished tabs).
 * Sorting is performed by the API.
 */
export const useVendorInvoiceJobLists = () => {
    const [pages, setPages] = useState(() => ({ ...INITIAL_PAGES }));

    const setSectionPage = useCallback((section, nextPage) => {
        setPages((prev) => ({
            ...prev,
            [section]: Math.max(1, nextPage),
        }));
    }, []);

    const pendingVendor = useJobsPage({
        status: JOB_STATUS.PENDING_VENDOR,
        page: pages.pendingVendor,
        pageSize: JOB_LIST_PAGE_SIZE,
        refetchInterval: VENDOR_INVOICE_POLLING.ACTIVE_LIST_MS,
        setPage: (p) => setSectionPage("pendingVendor", p),
    });
    const pendingReview = useJobsPage({
        status: JOB_STATUS.PENDING_REVIEW,
        page: pages.pendingReview,
        pageSize: JOB_LIST_PAGE_SIZE,
        refetchInterval: VENDOR_INVOICE_POLLING.ACTIVE_LIST_MS,
        setPage: (p) => setSectionPage("pendingReview", p),
    });
    const failed = useJobsPage({
        status: JOB_STATUS.FAILED,
        page: pages.failed,
        pageSize: JOB_LIST_PAGE_SIZE,
        refetchInterval: VENDOR_INVOICE_POLLING.ACTIVE_LIST_MS,
        setPage: (p) => setSectionPage("failed", p),
    });
    const processing = useJobsPage({
        status: PROCESSING_LIST_STATUS,
        page: pages.processing,
        pageSize: JOB_LIST_PAGE_SIZE,
        refetchInterval: VENDOR_INVOICE_POLLING.ACTIVE_LIST_MS,
        setPage: (p) => setSectionPage("processing", p),
    });
    const archived = useJobsPage({
        status: JOB_STATUS.FINISHED,
        page: pages.archived,
        pageSize: JOB_LIST_PAGE_SIZE,
        refetchInterval: VENDOR_INVOICE_POLLING.FINISHED_LIST_MS,
        setPage: (p) => setSectionPage("archived", p),
    });

    const error = [
        pendingVendor,
        pendingReview,
        failed,
        processing,
        archived,
    ].find((section) => section.isError)?.error;

    return {
        pendingVendor,
        pendingReview,
        failed,
        processing,
        archived,
        error,
    };
};
